import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { ArrowLeft, TrendingUp, BookOpen, Clock, Trophy, Target, MessageSquare, Calendar, Bot, Send, Loader2, X, Sparkles, User, Minimize2, Maximize2, Flame, Award, Star, Crown, Sun, Moon, Medal, Zap, Brain, FileText, ChevronRight, Home, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Badge icon mapping
const BADGE_ICONS = {
  trophy: Trophy,
  flame: Flame,
  crown: Crown,
  star: Star,
  book: BookOpen,
  medal: Medal,
  sun: Sun,
  moon: Moon,
};

// Available practice paper categories
const PRACTICE_CATEGORIES = [
  { id: 'seven-plus', name: '7+', subjects: ['maths', 'english', 'reasoning'] },
  { id: 'eleven-plus', name: '11+', subjects: ['maths', 'english', 'verbal-reasoning', 'non-verbal-reasoning'] },
  { id: 'year-9', name: 'Year 9', subjects: ['maths', 'english', 'science', 'history', 'geography'] },
  { id: 'year-10', name: 'Year 10', subjects: ['maths', 'english', 'science', 'history', 'geography'] },
  { id: 'gcse', name: 'GCSE', subjects: ['maths', 'english', 'sciences'] },
  { id: 'a-levels', name: 'A-Levels', subjects: ['maths-a', 'physics-a', 'chemistry-a', 'biology-a', 'english-lit-a'] },
];

const StudentDashboard = ({ progress }) => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [challengerData, setChallengerData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // AI Chat states
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(() => `dashboard_chat_${Date.now()}`);
  const chatScrollRef = useRef(null);

  useEffect(() => {
    loadDashboardData();
    loadStreakData();
    loadChallengerStats();
    recordActivity();
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [messages]);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/student/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const loadStreakData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/student/streak`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStreakData(response.data);
    } catch (error) {
      console.error('Error loading streak:', error);
    }
  };

  const loadChallengerStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/challenger/user-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallengerData(response.data);
    } catch (error) {
      console.error('Error loading challenger stats:', error);
    }
  };

  const recordActivity = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/student/record-activity`,
        { activityType: 'dashboard_visit' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show new badge notifications
      if (response.data.newBadges && response.data.newBadges.length > 0) {
        response.data.newBadges.forEach(badge => {
          toast.success(`🏆 New Badge: ${badge.name}!`, {
            description: badge.description
          });
        });
        // Reload streak data to show new badges
        loadStreakData();
      }
    } catch (error) {
      console.error('Error recording activity:', error);
    }
  };

  // AI Chat functions
  const sendChatMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = {
      role: 'user',
      content: chatInput,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = chatInput;
    setChatInput('');
    setChatLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai-chat/message`,
        { message: messageToSend, sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to get response. Please try again.');
    } finally {
      setChatLoading(false);
    }
  };

  const quickQuestions = [
    'How to solve algebra equations?',
    'Explain fractions simply',
    'Tips for reading comprehension'
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const stats = dashboardData || {
    totalTests: 0,
    averageScore: 0,
    timeSpent: 0,
    strongestSubject: 'N/A',
    recentTests: [],
    subjectProgress: [],
    performanceTrend: []
  };

  const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-6 px-4 md:px-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="text-white hover:bg-white/20"
                data-testid="back-btn"
              >
                <ArrowLeft className="mr-1 h-4 w-4" /> Back
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/categories')}
                className="text-white hover:bg-white/20"
              >
                <Home className="mr-1 h-4 w-4" /> Home
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setChatOpen(true)}
                className="bg-white text-purple-700 hover:bg-white/90"
                data-testid="ai-chat-button"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                AI Study Assistant
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  localStorage.clear();
                  window.location.replace('/');
                }}
                className="text-white hover:bg-white/20"
                data-testid="logout-btn"
              >
                <LogOut className="mr-1 h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            My Learning Dashboard
          </h1>
          <p className="text-white/90">Track your progress and improve your performance</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6">
        {/* Quick Stats */}
        <div className="grid md:grid-cols-5 gap-4">
          <Card className="shadow-xl" data-testid="stat-tests">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Tests Completed</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.totalTests}</p>
                </div>
                <BookOpen className="h-10 w-10 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-xl" data-testid="stat-average">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Average Score</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.averageScore}%</p>
                </div>
                <Trophy className="h-10 w-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-xl" data-testid="stat-time">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Study Time</p>
                  <p className="text-3xl font-bold text-green-600">{stats.timeSpent}h</p>
                </div>
                <Clock className="h-10 w-10 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-xl" data-testid="stat-strongest">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Strongest Subject</p>
                  <p className="text-xl font-bold text-orange-600">{stats.strongestSubject}</p>
                </div>
                <Target className="h-10 w-10 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-xl bg-gradient-to-br from-yellow-400 to-orange-500 text-white" data-testid="stat-challenger">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80">Challenger Best</p>
                  <p className="text-3xl font-bold">{challengerData?.best_score || 0}</p>
                </div>
                <Zap className="h-10 w-10 opacity-90" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Streak & Badges Section */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Streak Card */}
          <Card className="shadow-xl bg-gradient-to-br from-orange-500 to-red-500 text-white" data-testid="streak-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80">Current Study Streak</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Flame className="h-8 w-8 animate-pulse" />
                    <p className="text-5xl font-bold">{streakData?.currentStreak || 0}</p>
                    <span className="text-xl">days</span>
                  </div>
                  <p className="text-sm text-white/80 mt-2">
                    Longest streak: {streakData?.longestStreak || 0} days
                  </p>
                </div>
                <div className="text-right">
                  <div className="bg-white/20 rounded-full p-4">
                    <Flame className="h-12 w-12" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Badges Card */}
          <Card className="shadow-xl" data-testid="badges-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600">Badges Earned</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {streakData?.badges?.length || 0}
                  </p>
                </div>
                <Award className="h-10 w-10 text-purple-600" />
              </div>
              <div className="flex flex-wrap gap-2">
                {streakData?.badges?.slice(0, 5).map((badge, idx) => {
                  const IconComponent = BADGE_ICONS[badge.icon] || Trophy;
                  return (
                    <div 
                      key={idx} 
                      className="flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs"
                      title={badge.description}
                    >
                      <IconComponent className="h-3 w-3" />
                      {badge.name}
                    </div>
                  );
                })}
                {(!streakData?.badges || streakData.badges.length === 0) && (
                  <p className="text-sm text-gray-500">Complete activities to earn badges!</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="challenger">🔥 Challenger</TabsTrigger>
            <TabsTrigger value="papers">📝 Papers</TabsTrigger>
            <TabsTrigger value="badges">Badges</TabsTrigger>
            <TabsTrigger value="subjects">By Subject</TabsTrigger>
            <TabsTrigger value="history">Test History</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Performance Trend */}
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Performance Trend
                  </CardTitle>
                  <CardDescription>Your score progression over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={stats.performanceTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="score" stroke="#8b5cf6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Subject Distribution */}
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle>Subject Performance</CardTitle>
                  <CardDescription>Average scores by subject</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={stats.subjectProgress}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name}: ${entry.score}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="score"
                      >
                        {stats.subjectProgress.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Recent Tests
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.recentTests.length > 0 ? (
                    stats.recentTests.map((test, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-semibold">{test.subject}</p>
                          <p className="text-sm text-gray-600">{test.date}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-purple-600">{test.score}%</p>
                          <Badge variant={test.passed ? 'default' : 'destructive'}>
                            {test.passed ? 'Passed' : 'Need Improvement'}
                          </Badge>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-8">No tests completed yet. Start practicing!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="challenger" className="space-y-6">
            <Card className="shadow-xl">
              <CardHeader className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2"><Zap className="h-6 w-6" />Challenger Mode</CardTitle>
                <CardDescription className="text-white/90">AI-powered adaptive difficulty challenges - test your limits!</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                {challengerData && challengerData.total_sessions > 0 ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-purple-50 p-4 rounded-xl text-center">
                        <Brain className="h-6 w-6 text-purple-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-purple-800">{challengerData.best_difficulty}</p>
                        <p className="text-xs text-purple-600">Max Difficulty</p>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-xl text-center">
                        <Trophy className="h-6 w-6 text-yellow-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-yellow-800">{challengerData.best_score}</p>
                        <p className="text-xs text-yellow-600">Best Score</p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-xl text-center">
                        <Flame className="h-6 w-6 text-orange-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-orange-800">{challengerData.best_streak}</p>
                        <p className="text-xs text-orange-600">Best Streak</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-xl text-center">
                        <Target className="h-6 w-6 text-green-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-green-800">{challengerData.average_accuracy}%</p>
                        <p className="text-xs text-green-600">Avg Accuracy</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-3">Recent Sessions ({challengerData.total_sessions} total)</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {challengerData.recent_sessions?.map((session, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium capitalize">{session.subject?.replace(/-/g, ' ')} - {session.stage?.toUpperCase()}</p>
                              <p className="text-xs text-gray-500">{new Date(session.completed_at).toLocaleDateString()}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-purple-600">{session.score} pts</p>
                              <p className="text-xs text-gray-500">Level {session.max_difficulty}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button 
                      onClick={() => navigate('/categories')} 
                      className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                      data-testid="play-challenger-btn"
                    >
                      <Zap className="mr-2 h-4 w-4" /> Play Challenger Mode
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Zap className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No Challenger Sessions Yet</h3>
                    <p className="text-gray-500 mb-4">Challenge yourself with AI-generated questions that adapt to your skill level!</p>
                    <Button 
                      onClick={() => navigate('/categories')} 
                      className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                      data-testid="start-challenger-btn"
                    >
                      <Zap className="mr-2 h-4 w-4" /> Start Your First Challenge
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="papers" className="space-y-6">
            {/* My Growth Zone - Featured */}
            <Card className="shadow-xl bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 text-white overflow-hidden">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className="p-4 bg-white/20 rounded-2xl">
                      <Sparkles className="h-10 w-10 text-yellow-300" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-1">My Growth Zone</h3>
                      <p className="text-white/80 max-w-md">
                        Turn gaps into strengths! Choose your topics, difficulty level, and year group to focus on areas that need improvement.
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="lg"
                    className="bg-white text-purple-700 hover:bg-purple-100 shrink-0"
                    onClick={() => navigate('/ai-practice')}
                    data-testid="ai-practice-generator-btn"
                  >
                    <Sparkles className="mr-2 h-5 w-5" />
                    Start Growing
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Practice Papers</CardTitle>
                <CardDescription>Quick access to practice and mock papers across all exam stages</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {PRACTICE_CATEGORIES.map((category) => (
                    <Card 
                      key={category.id} 
                      className="border-2 hover:border-purple-300 transition-colors cursor-pointer"
                      onClick={() => navigate(`/subjects/${category.id}`)}
                      data-testid={`paper-category-${category.id}`}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center justify-between">
                          {category.name}
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {category.subjects.slice(0, 3).map((subject) => (
                            <div key={subject} className="flex items-center justify-between text-sm">
                              <span className="capitalize">{subject.replace(/-/g, ' ')}</span>
                              <Badge variant="outline" className="text-xs">10 papers</Badge>
                            </div>
                          ))}
                          {category.subjects.length > 3 && (
                            <p className="text-xs text-gray-500">+{category.subjects.length - 3} more subjects</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Quick Start */}
                <div className="mt-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">Ready to Practice?</h3>
                      <p className="text-sm text-gray-600">Jump into a practice paper or test your skills in Challenger Mode!</p>
                    </div>
                    <div className="flex gap-3">
                      <Button 
                        onClick={() => navigate('/categories')} 
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="browse-papers-btn"
                      >
                        <BookOpen className="mr-2 h-4 w-4" /> Browse Papers
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="badges" className="space-y-6">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  My Badges
                </CardTitle>
                <CardDescription>Achievements you&apos;ve earned through learning</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {streakData?.badges?.map((badge, idx) => {
                    const IconComponent = BADGE_ICONS[badge.icon] || Trophy;
                    return (
                      <div 
                        key={idx}
                        className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 text-center border-2 border-purple-200 hover:border-purple-400 transition-colors"
                        data-testid={`badge-${badge.id}`}
                      >
                        <div className="bg-gradient-to-br from-purple-500 to-indigo-500 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                          <IconComponent className="h-8 w-8" />
                        </div>
                        <h4 className="font-bold text-gray-800">{badge.name}</h4>
                        <p className="text-xs text-gray-600 mt-1">{badge.description}</p>
                        <p className="text-xs text-purple-600 mt-2">
                          {new Date(badge.earnedAt).toLocaleDateString()}
                        </p>
                      </div>
                    );
                  })}
                </div>
                {(!streakData?.badges || streakData.badges.length === 0) && (
                  <div className="text-center py-12">
                    <Award className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No badges yet!</h3>
                    <p className="text-gray-500 mb-4">Complete tests and maintain your study streak to earn badges</p>
                    <Button onClick={() => navigate('/categories')} className="bg-purple-600 hover:bg-purple-700">
                      Start Learning
                    </Button>
                  </div>
                )}

                {/* Available Badges */}
                <div className="mt-8 border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Target className="h-5 w-5" /> Available Badges
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {streakData?.availableBadges && Object.entries(streakData.availableBadges)
                      .filter(([id]) => !streakData.badges?.some(b => b.id === id))
                      .slice(0, 8)
                      .map(([id, badge]) => {
                        const IconComponent = BADGE_ICONS[badge.icon] || Trophy;
                        return (
                          <div 
                            key={id}
                            className="bg-gray-100 rounded-lg p-3 text-center opacity-60"
                          >
                            <div className="bg-gray-300 text-gray-500 rounded-full w-10 h-10 flex items-center justify-center mx-auto mb-2">
                              <IconComponent className="h-5 w-5" />
                            </div>
                            <h5 className="text-sm font-medium text-gray-600">{badge.name}</h5>
                            <p className="text-xs text-gray-500">{badge.description}</p>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subjects" className="space-y-6">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle>Progress by Subject</CardTitle>
                <CardDescription>Detailed breakdown of your performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {stats.subjectProgress.map((subject, idx) => (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                          <span className="font-semibold">{subject.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-xl font-bold text-purple-600">{subject.score}%</span>
                          <p className="text-xs text-gray-600">{subject.tests} tests</p>
                        </div>
                      </div>
                      <Progress value={subject.score} className="h-3" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle>Complete Test History</CardTitle>
                <CardDescription>All your attempted tests and scores</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {stats.recentTests.length > 0 ? (
                    stats.recentTests.map((test, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 border-2 rounded-lg hover:border-purple-400 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline">{test.type}</Badge>
                            <span className="font-semibold">{test.subject}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            Paper #{test.paperNumber} • {test.date} • {test.duration} mins
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-purple-600">{test.score}%</p>
                          <p className="text-sm text-gray-600">{test.correct}/{test.total} correct</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-600 mb-4">No test history available</p>
                      <Button onClick={() => navigate('/categories')} className="bg-purple-600 hover:bg-purple-700">
                        Take Your First Test
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Floating AI Chat Button */}
      {!chatOpen && (
        <button
          onClick={() => setChatOpen(true)}
          className="fixed bottom-20 right-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform z-50 flex items-center gap-2"
          data-testid="open-chat-btn"
        >
          <Sparkles className="h-6 w-6" />
          <span className="hidden md:inline font-semibold">AI Assistant</span>
        </button>
      )}

      {/* AI Chat Widget */}
      {chatOpen && (
        <div 
          className={`fixed right-6 bg-white rounded-2xl shadow-2xl z-50 flex flex-col transition-all duration-300 ${
            chatMinimized 
              ? 'bottom-20 w-80 h-14' 
              : 'bottom-20 w-96 h-[500px]'
          }`}
          data-testid="chat-widget"
        >
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm">AI Study Assistant</h3>
                <p className="text-xs text-white/80">Ask me anything!</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setChatMinimized(!chatMinimized)}
                className="hover:bg-white/20 p-1.5 rounded-lg transition-colors"
                data-testid="minimize-chat-btn"
              >
                {chatMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </button>
              <button 
                onClick={() => setChatOpen(false)}
                className="hover:bg-white/20 p-1.5 rounded-lg transition-colors"
                data-testid="close-chat-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Chat Content - Hidden when minimized */}
          {!chatMinimized && (
            <>
              {/* Messages Area */}
              <div 
                ref={chatScrollRef}
                className="flex-1 p-4 overflow-y-auto space-y-3"
              >
                {messages.length === 0 ? (
                  <div className="text-center space-y-4 py-4">
                    <div className="bg-purple-100 p-4 rounded-full w-fit mx-auto">
                      <Sparkles className="h-8 w-8 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">Hi! I&apos;m your AI tutor</p>
                      <p className="text-sm text-gray-600">Ask me anything about your studies</p>
                    </div>
                    <div className="space-y-2">
                      {quickQuestions.map((q, idx) => (
                        <button
                          key={idx}
                          onClick={() => setChatInput(q)}
                          className="w-full text-left p-2 bg-gray-50 hover:bg-purple-50 rounded-lg text-xs transition-colors border hover:border-purple-300"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`chat-message-${idx}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <Bot className="h-4 w-4" />
                        </div>
                      )}
                      <div
                        className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${
                          msg.role === 'user'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      {msg.role === 'user' && (
                        <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                  ))
                )}
                {chatLoading && (
                  <div className="flex gap-2 justify-start">
                    <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center">
                      <Bot className="h-4 w-4" />
                    </div>
                    <div className="bg-gray-100 rounded-xl px-3 py-2">
                      <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                    </div>
                  </div>
                )}
              </div>

              {/* Chat Input */}
              <div className="border-t p-3">
                <form onSubmit={sendChatMessage} className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Ask a question..."
                    className="flex-1 text-sm"
                    disabled={chatLoading}
                    data-testid="chat-input"
                  />
                  <Button
                    type="submit"
                    disabled={!chatInput.trim() || chatLoading}
                    className="bg-purple-600 hover:bg-purple-700 px-3"
                    data-testid="chat-send-btn"
                  >
                    {chatLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </form>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;
